import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, SendIcon, FlameIcon, StoreIcon } from 'lucide-react';
interface OwnerFormProps {
  onBack: () => void;
}
const CANTON_OPTIONS = ['Tarrazú', 'Dota', 'León Cortés'];
const TIPO_OPTIONS = ['Restaurante', 'Soda', 'Cafetería', 'Ventanita', 'Bar', 'Otro'];
const PRECIO_OPTIONS = ['$ — Económico', '$$ — Moderado', '$$$ — Premium'];
const ANTIGUEDAD_OPTIONS = ['Menos de 1 año', '1 a 3 años', '3 a 5 años', 'Más de 5 años'];
interface FormData {
  nombreRestaurante: string;
  nombreDueno: string;
  telefono: string;
  canton: string;
  tipoNegocio: string;
  antiguedad: string;
  redesSociales: string;
  tipoComida: string;
  rangoPrecio: string;
  comentarios: string;
}
const initialForm: FormData = {
  nombreRestaurante: '',
  nombreDueno: '',
  telefono: '',
  canton: '',
  tipoNegocio: '',
  antiguedad: '',
  redesSociales: '',
  tipoComida: '',
  rangoPrecio: '',
  comentarios: ''
};
export function OwnerForm({
  onBack
}: OwnerFormProps) {
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const update = (field: keyof FormData, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!form.nombreRestaurante.trim()) newErrors.nombreRestaurante = 'Requerido';
    if (!form.nombreDueno.trim()) newErrors.nombreDueno = 'Requerido';
    if (!form.telefono.trim()) newErrors.telefono = 'Requerido';
    if (!form.canton) newErrors.canton = 'Seleccioná un cantón';
    if (!form.tipoNegocio) newErrors.tipoNegocio = 'Seleccioná un tipo';
    if (!form.tipoComida.trim()) newErrors.tipoComida = 'Requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const message = ['🔥 *NUEVO RESTAURANTE INTERESADO — DISTRITO LS*', '', `🏪 *Restaurante:* ${form.nombreRestaurante}`, `👤 *Dueño/Encargado:* ${form.nombreDueno}`, `📞 *Teléfono:* ${form.telefono}`, `📍 *Cantón:* ${form.canton}`, `🍽️ *Tipo de negocio:* ${form.tipoNegocio}`, `⏳ *Antigüedad:* ${form.antiguedad || 'No indicó'}`, `📱 *Redes sociales:* ${form.redesSociales || 'No indicó'}`, `🥘 *Tipo de comida:* ${form.tipoComida}`, `💰 *Rango de precios:* ${form.rangoPrecio || 'No indicó'}`, form.comentarios ? `💬 *Comentarios:* ${form.comentarios}` : ''].filter(Boolean).join('\n');
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/50685316690?text=${encoded}`, '_blank');
  };
  const inputClass = (field: keyof FormData) => `w-full px-4 py-3 rounded-xl bg-bg-secondary border ${errors[field] ? 'border-red-500' : 'border-border'} text-text-primary font-body placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-brand-gold/50 focus:border-brand-gold/50 transition-colors`;
  const selectClass = (field: keyof FormData) => `w-full px-4 py-3 rounded-xl bg-bg-secondary border ${errors[field] ? 'border-red-500' : 'border-border'} text-text-primary font-body focus:outline-none focus:ring-1 focus:ring-brand-gold/50 focus:border-brand-gold/50 transition-colors appearance-none cursor-pointer`;
  return <div className="min-h-screen bg-bg-primary pb-24" data-id="element-180">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border pt-10 pb-8 px-4" data-id="element-181">
        <div className="max-w-3xl mx-auto" data-id="element-182">
          <button onClick={onBack} className="flex items-center gap-2 text-text-secondary hover:text-brand-gold font-body text-sm font-medium mb-6 transition-colors" data-id="element-183">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-184" /> Volver al inicio
          </button>

          <div className="flex items-center gap-3 mb-3" data-id="element-185">
            <div className="w-12 h-12 rounded-full bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center" data-id="element-186">
              <StoreIcon className="w-6 h-6 text-brand-gold" data-id="element-187" />
            </div>
            <div data-id="element-188">
              <h1 className="text-4xl md:text-5xl font-heading uppercase tracking-wide text-white" data-id="element-189">
                Uní tu restaurante
              </h1>
            </div>
          </div>
          <p className="font-body text-text-secondary text-lg mt-3 max-w-2xl" data-id="element-190">
            Completá este formulario y nos pondremos en contacto con vos para
            hablar sobre cómo Distrito LS puede ayudar a tu negocio.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 pt-10" data-id="element-191">
        <motion.form onSubmit={handleSubmit} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="space-y-8" data-id="element-192">
          {/* Section: Info del negocio */}
          <div data-id="element-193">
            <div className="flex items-center gap-2 mb-6" data-id="element-194">
              <FlameIcon className="w-5 h-5 text-brand-orange" data-id="element-195" />
              <h2 className="font-heading text-2xl text-white uppercase tracking-wide" data-id="element-196">
                Sobre tu negocio
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-197">
              <div data-id="element-198">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-199">
                  Nombre del restaurante{' '}
                  <span className="text-brand-orange" data-id="element-200">*</span>
                </label>
                <input type="text" value={form.nombreRestaurante} onChange={e => update('nombreRestaurante', e.target.value)} placeholder="Ej: Soda Doña Flor" className={inputClass('nombreRestaurante')} data-id="element-201" />
                {errors.nombreRestaurante && <p className="mt-1 text-xs text-red-400 font-body" data-id="element-202">
                    {errors.nombreRestaurante}
                  </p>}
              </div>

              <div data-id="element-203">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-204">
                  Tipo de negocio <span className="text-brand-orange" data-id="element-205">*</span>
                </label>
                <select value={form.tipoNegocio} onChange={e => update('tipoNegocio', e.target.value)} className={selectClass('tipoNegocio')} data-id="element-206">
                  <option value="" data-id="element-207">Seleccioná...</option>
                  {TIPO_OPTIONS.map(t => <option key={t} value={t} data-id="element-208">
                      {t}
                    </option>)}
                </select>
                {errors.tipoNegocio && <p className="mt-1 text-xs text-red-400 font-body" data-id="element-209">
                    {errors.tipoNegocio}
                  </p>}
              </div>

              <div data-id="element-210">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-211">
                  Cantón <span className="text-brand-orange" data-id="element-212">*</span>
                </label>
                <select value={form.canton} onChange={e => update('canton', e.target.value)} className={selectClass('canton')} data-id="element-213">
                  <option value="" data-id="element-214">Seleccioná...</option>
                  {CANTON_OPTIONS.map(c => <option key={c} value={c} data-id="element-215">
                      {c}
                    </option>)}
                </select>
                {errors.canton && <p className="mt-1 text-xs text-red-400 font-body" data-id="element-216">
                    {errors.canton}
                  </p>}
              </div>

              <div data-id="element-217">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-218">
                  Antigüedad del negocio
                </label>
                <select value={form.antiguedad} onChange={e => update('antiguedad', e.target.value)} className={selectClass('antiguedad')} data-id="element-219">
                  <option value="" data-id="element-220">Seleccioná...</option>
                  {ANTIGUEDAD_OPTIONS.map(a => <option key={a} value={a} data-id="element-221">
                      {a}
                    </option>)}
                </select>
              </div>

              <div className="md:col-span-2" data-id="element-222">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-223">
                  ¿Qué tipo de comida ofrecen?{' '}
                  <span className="text-brand-orange" data-id="element-224">*</span>
                </label>
                <input type="text" value={form.tipoComida} onChange={e => update('tipoComida', e.target.value)} placeholder="Ej: Comida típica, casados, hamburguesas, café de especialidad..." className={inputClass('tipoComida')} data-id="element-225" />
                {errors.tipoComida && <p className="mt-1 text-xs text-red-400 font-body" data-id="element-226">
                    {errors.tipoComida}
                  </p>}
              </div>

              <div data-id="element-227">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-228">
                  Rango de precios
                </label>
                <select value={form.rangoPrecio} onChange={e => update('rangoPrecio', e.target.value)} className={selectClass('rangoPrecio')} data-id="element-229">
                  <option value="" data-id="element-230">Seleccioná...</option>
                  {PRECIO_OPTIONS.map(p => <option key={p} value={p} data-id="element-231">
                      {p}
                    </option>)}
                </select>
              </div>

              <div data-id="element-232">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-233">
                  Redes sociales (Instagram/Facebook)
                </label>
                <input type="text" value={form.redesSociales} onChange={e => update('redesSociales', e.target.value)} placeholder="Ej: @sodadonaflor" className={inputClass('redesSociales')} data-id="element-234" />
              </div>
            </div>
          </div>

          {/* Section: Info de contacto */}
          <div data-id="element-235">
            <div className="flex items-center gap-2 mb-6" data-id="element-236">
              <FlameIcon className="w-5 h-5 text-brand-orange" data-id="element-237" />
              <h2 className="font-heading text-2xl text-white uppercase tracking-wide" data-id="element-238">
                Contacto
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-239">
              <div data-id="element-240">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-241">
                  Nombre del dueño o encargado{' '}
                  <span className="text-brand-orange" data-id="element-242">*</span>
                </label>
                <input type="text" value={form.nombreDueno} onChange={e => update('nombreDueno', e.target.value)} placeholder="Ej: Juan Pérez" className={inputClass('nombreDueno')} data-id="element-243" />
                {errors.nombreDueno && <p className="mt-1 text-xs text-red-400 font-body" data-id="element-244">
                    {errors.nombreDueno}
                  </p>}
              </div>

              <div data-id="element-245">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-246">
                  Teléfono de contacto{' '}
                  <span className="text-brand-orange" data-id="element-247">*</span>
                </label>
                <input type="tel" value={form.telefono} onChange={e => update('telefono', e.target.value)} placeholder="Ej: 8888-8888" className={inputClass('telefono')} data-id="element-248" />
                {errors.telefono && <p className="mt-1 text-xs text-red-400 font-body" data-id="element-249">
                    {errors.telefono}
                  </p>}
              </div>

              <div className="md:col-span-2" data-id="element-250">
                <label className="block font-body text-sm font-semibold text-text-secondary mb-2" data-id="element-251">
                  ¿Algo más que quieras contarnos?
                </label>
                <textarea value={form.comentarios} onChange={e => update('comentarios', e.target.value)} placeholder="Horarios, especialidades, lo que quieras..." rows={3} className={`${inputClass('comentarios')} resize-none`} data-id="element-252" />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4 pb-8" data-id="element-253">
            <button type="submit" className="w-full md:w-auto bg-gradient-fire text-white font-body font-bold px-12 py-4 rounded-full hover:shadow-fire transition-all flex items-center justify-center gap-3 text-lg mx-auto" data-id="element-254">
              <SendIcon className="w-5 h-5" data-id="element-255" />
              Enviar por WhatsApp
            </button>
            <p className="text-center mt-4 font-body text-sm text-text-muted" data-id="element-256">
              Al enviar, se abrirá WhatsApp con tu información para que nos
              contactés directamente.
            </p>
          </div>
        </motion.form>
      </div>
    </div>;
}